import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import type { CSSProperties } from 'react';
import { EmptyApps } from './components/EmptyApps';
import { LeadDrawer } from './components/LeadDrawer';
import { LoginView } from './components/LoginView';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { useSession } from './lib/auth';
import { queryClient } from './lib/query';
import { ShellProvider, useShell } from './shell';
import { CallingView } from './features/calling/CallingView';
import { CostsView } from './features/costs/CostsView';
import { DashboardView } from './features/dashboard/DashboardView';
import { PipelineView } from './features/pipeline/PipelineView';
import { ResearchView } from './features/research/ResearchView';

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/research': 'Research',
  '/pipeline': 'Pipeline',
  '/calling': 'Dzwonienie',
  '/costs': 'Koszty',
};

function AppShell() {
  const { openLeadId, closeLead, prefs, apps, appsLoading } = useShell();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const title = TITLES[pathname] ?? 'Dashboard';
  const style = {
    '--acc': prefs.accent,
    '--rowpy': prefs.density === 'kompaktowa' ? '6px' : '10px',
  } as CSSProperties;

  const noApps = !appsLoading && apps.length === 0;

  return (
    <div className={`shell${prefs.zebra ? ' zebra' : ''}`} style={style}>
      <Sidebar />
      <main className="main">
        <Topbar title={title} />
        <div className="content">{noApps ? <EmptyApps /> : <Outlet />}</div>
      </main>
      {openLeadId ? <LeadDrawer leadId={openLeadId} onClose={closeLead} /> : null}
    </div>
  );
}

function RootGate() {
  const { data: session, isPending } = useSession();
  if (isPending) {
    return (
      <div className="center-pad">
        <span className="spinner" /> Wczytywanie…
      </div>
    );
  }
  if (!session) return <LoginView />;
  return (
    <ShellProvider>
      <AppShell />
    </ShellProvider>
  );
}

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({ component: RootGate });

const dashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: DashboardView });
const researchRoute = createRoute({ getParentRoute: () => rootRoute, path: '/research', component: ResearchView });
const pipelineRoute = createRoute({ getParentRoute: () => rootRoute, path: '/pipeline', component: PipelineView });
const callingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/calling', component: CallingView });
const costsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/costs', component: CostsView });

const routeTree = rootRoute.addChildren([dashboardRoute, researchRoute, pipelineRoute, callingRoute, costsRoute]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
